import React, { useState, useEffect, useRef } from 'react';
import { CalendarBlank, Phone } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { BRAND } from '@/theme/brand';
import { saveLeadInBackground } from '@/lib/saveLead';
import { openWhatsApp } from '@/lib/whatsapp';
import { useVehicles } from '@/hooks/useVehicles';
import CityInput from '@/components/CityInput';
import TripTypeSelector from '@/components/booking/TripTypeSelector';
import { TRIP_TYPES } from '@/lib/cabServices';
import { validateBookingForm, normalizeIndianPhone } from '@/lib/bookingValidation';

const todayStr = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const formatDateDisplay = (isoDate) => {
  if (!isoDate) return '';
  const [y, m, d] = isoDate.split('-');
  if (!y || !m || !d) return isoDate;
  return `${d}-${m}-${y}`;
};

const TRIP_LABELS = {
  one_way: 'One Way',
  round_trip: 'Round Trip',
  local_rental: 'Local Rental',
  airport_transfer: 'Airport Transfer',
};

const defaultForm = {
  trip_type: 'one_way',
  vehicle: '',
  pickup: '',
  drop: '',
  date: '',
  passengers: '1',
  name: '',
  phone: '',
  email: '',
  notes: '',
};

const QUOTE_SUCCESS_KEY = 'atithi_quote_success';
const QUOTE_WA_KEY = 'atithi_quote_whatsapp';

const readStoredQuote = () => {
  try {
    const raw = sessionStorage.getItem(QUOTE_SUCCESS_KEY);
    if (!raw) return null;
    return {
      success: JSON.parse(raw),
      whatsappUrl: sessionStorage.getItem(QUOTE_WA_KEY) || '',
    };
  } catch {
    return null;
  }
};

const storeQuoteSuccess = (successData, waUrl) => {
  sessionStorage.setItem(QUOTE_SUCCESS_KEY, JSON.stringify(successData));
  sessionStorage.setItem(QUOTE_WA_KEY, waUrl);
};

const clearStoredQuote = () => {
  sessionStorage.removeItem(QUOTE_SUCCESS_KEY);
  sessionStorage.removeItem(QUOTE_WA_KEY);
};

const buildInquiryMessage = (form, tripLabel) => {
  const name = form.name.trim();
  const phone = form.phone.trim();
  const email = form.email.trim();
  return `
🙏 *New Cab Booking Inquiry*
━━━━━━━━━━━━━━━━━━
👤 *Name:* ${name}
📱 *Phone:* ${phone}
${email ? `📧 *Email:* ${email}` : ''}
━━━━━━━━━━━━━━━━━━
🚗 *Trip Type:* ${tripLabel}
🚙 *Vehicle:* ${form.vehicle}
👥 *Passengers:* ${form.passengers}
━━━━━━━━━━━━━━━━━━
📍 *From:* ${form.pickup.trim()}
📍 *To:* ${form.drop.trim()}
📅 *Date:* ${formatDateDisplay(form.date) || form.date}
━━━━━━━━━━━━━━━━━━
${form.notes.trim() ? `📝 *Notes:* ${form.notes.trim()}` : ''}
Please confirm availability and share the fare. Thank you! 🙏
  `.trim();
};

const CabBookingForm = ({
  initialTripType = 'one_way',
  initialPickup = '',
  initialDrop = '',
  leadSource = 'Book Cab Page',
  variant = 'default',
}) => {
  const { bookingOptions, vehicles } = useVehicles({ activeOnly: true });
  const [form, setForm] = useState({
    ...defaultForm,
    trip_type: initialTripType,
    pickup: initialPickup,
    drop: initialDrop,
    date: todayStr(),
  });
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(null);
  const [whatsappUrl, setWhatsappUrl] = useState('');
  const formRef = useRef(null);

  useEffect(() => {
    if (bookingOptions.length && !form.vehicle) {
      setForm((p) => ({ ...p, vehicle: bookingOptions[0].value }));
    }
  }, [bookingOptions, form.vehicle]);

  useEffect(() => {
    const applyStoredQuote = () => {
      const stored = readStoredQuote();
      if (stored) {
        setSuccess(stored.success);
        setWhatsappUrl(stored.whatsappUrl);
      }
    };

    applyStoredQuote();

    const onPageShow = (e) => {
      if (e.persisted) applyStoredQuote();
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') applyStoredQuote();
    };

    window.addEventListener('pageshow', onPageShow);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('pageshow', onPageShow);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  const set = (field, value) => {
    setForm((p) => ({ ...p, [field]: value }));
    setErrors((p) => ({ ...p, [field]: '' }));
  };

  const tripLabel = TRIP_LABELS[form.trip_type] || TRIP_TYPES.find((t) => t.id === form.trip_type)?.label || form.trip_type;

  const validate = () => {
    const next = validateBookingForm(form, { vehicles });
    setErrors(next);
    if (Object.keys(next).length > 0) {
      setTimeout(() => {
        const el = formRef.current?.querySelector('[data-field-error="true"]');
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
    }
    return Object.keys(next).length === 0;
  };

  const handleInstantQuote = () => {
    if (!validate()) return;

    const phone = normalizeIndianPhone(form.phone);

    const payload = {
      name: form.name.trim(),
      phone,
      email: form.email.trim() || null,
      from_city: form.pickup.trim(),
      to_city: form.drop.trim(),
      travel_date: formatDateDisplay(form.date) || form.date,
      vehicle: form.vehicle,
      passengers: parseInt(form.passengers, 10) || 1,
      trip_type: tripLabel,
      notes: form.notes.trim() || null,
      source: leadSource,
      status: 'New',
    };

    const msg = buildInquiryMessage({ ...form, phone }, tripLabel);
    const url = `https://wa.me/${BRAND.contact.phone}?text=${encodeURIComponent(msg)}`;

    const successData = {
      pickup: form.pickup.trim(),
      drop: form.drop.trim(),
      date: form.date,
      vehicle: form.vehicle,
      passengers: form.passengers,
    };

    storeQuoteSuccess(successData, url);
    setWhatsappUrl(url);
    setSuccess(successData);

    saveLeadInBackground(payload);
    openWhatsApp(url);
  };

  const resetForm = () => {
    clearStoredQuote();
    setForm({
      ...defaultForm,
      trip_type: initialTripType,
      date: todayStr(),
      vehicle: bookingOptions[0]?.value || '',
    });
    setErrors({});
    setSuccess(null);
    setWhatsappUrl('');
  };

  if (success) {
    return (
      <div className="font-body text-center py-4">
        <div style={{ fontSize: '64px', lineHeight: 1 }} aria-hidden>
          ✅
        </div>
        <h2 className="font-display mt-4" style={{ fontSize: '32px', color: '#1B2D5E' }}>
          Quote Sent!
        </h2>
        <p className="mt-3 mx-auto max-w-md" style={{ fontSize: '15px', color: '#666' }}>
          Your details are saved and sent on WhatsApp. Our team will confirm your fare within 2 minutes.
        </p>
        <div
          className="text-left mx-auto max-w-md mt-6"
          style={{ background: '#F8F8F8', borderRadius: '10px', padding: '20px 24px', fontSize: '14px', color: '#333', lineHeight: 2 }}
        >
          <div>📍 {success.pickup} → {success.drop}</div>
          <div>📅 {formatDateDisplay(success.date) || success.date}</div>
          <div>🚙 {success.vehicle} · {success.passengers} pax</div>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8 max-w-md mx-auto">
          <button
            type="button"
            onClick={() => whatsappUrl && window.open(whatsappUrl, '_blank')}
            className="font-body font-semibold text-white rounded-[10px] h-[54px] px-6"
            style={{ background: '#25D366' }}
          >
            💬 Open WhatsApp
          </button>
          <button
            type="button"
            onClick={resetForm}
            className="font-body font-semibold rounded-[10px] h-[54px] px-6 bg-white"
            style={{ border: '1px solid #1B2D5E', color: '#1B2D5E' }}
          >
            ← Book Another Cab
          </button>
        </div>
      </div>
    );
  }

  const fieldWrap = (field, children) => (
    <div data-field-error={errors[field] ? 'true' : undefined}>{children}</div>
  );

  const inputClass = (field) =>
    [
      'w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#E8651A]/30 focus:border-[#E8651A] transition-all duration-150 placeholder:text-gray-300 h-[42px]',
      errors[field] ? 'border-[#E53935] ring-[#E53935]/20' : '',
    ]
      .filter(Boolean)
      .join(' ');

  const selectTriggerClass = (field) =>
    [
      'w-full px-3 py-2.5 text-sm border rounded-xl bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#E8651A]/30 focus:border-[#E8651A] transition-all duration-150 h-[42px]',
      errors[field] ? 'border-[#E53935]' : 'border-gray-200',
    ]
      .filter(Boolean)
      .join(' ');

  const fieldLabelClass =
    variant === 'hero'
      ? 'block text-[9px] font-bold text-gray-400 uppercase tracking-wide mb-1'
      : 'block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1';

  const tripSelectorVariant = variant === 'hero' ? 'hero' : 'default';

  const swapLocations = () => {
    setForm((p) => ({ ...p, pickup: p.drop, drop: p.pickup }));
    setErrors((p) => ({ ...p, pickup: '', drop: '' }));
  };

  return (
    <form ref={formRef} onSubmit={(e) => e.preventDefault()} className="w-full font-body">
      <div className={variant === 'hero' ? '' : 'flex gap-2 mb-4 flex-wrap'} data-field-error={errors.trip_type ? 'true' : undefined}>
        <TripTypeSelector value={form.trip_type} onChange={(id) => set('trip_type', id)} variant={tripSelectorVariant} />
        {errors.trip_type && <p className="text-[11px] text-[#E53935] w-full">⚠ {errors.trip_type}</p>}
      </div>

      {/* ROW 1 — Primary hero bar */}
      <div className="flex flex-col lg:flex-row items-stretch gap-2 mb-3">
        {fieldWrap(
          'pickup',
          <div className={`flex-1 min-w-0 ${variant === 'hero' ? 'bg-[#FFF8F5] border-[1.5px] border-[#E8651A]/30 rounded-xl px-3 py-2' : ''}`}>
            <label className={fieldLabelClass} htmlFor="pickup-location">
              {variant === 'hero' ? '📍 From' : '📍 Pickup Location'}
            </label>
            <CityInput
              label=""
              placeholder="e.g. Rajkot, Ahmedabad..."
              value={form.pickup}
              onChange={(val) => set('pickup', val)}
              required
              error={errors.pickup}
              inputId="pickup-location"
            />
          </div>,
        )}

        <div className="hidden lg:flex items-end pb-2">
          <button
            type="button"
            onClick={swapLocations}
            className="text-[#E8651A] text-xl font-bold cursor-pointer hover:scale-110 transition-transform select-none bg-transparent border-0 p-0"
            title="Swap locations"
            aria-label="Swap pickup and drop locations"
          >
            ⇄
          </button>
        </div>

        {fieldWrap(
          'drop',
          <div className={`flex-1 min-w-0 ${variant === 'hero' ? 'bg-[#FFF8F5] border-[1.5px] border-[#E8651A]/30 rounded-xl px-3 py-2' : ''}`}>
            <label className={fieldLabelClass} htmlFor="drop-location">
              {variant === 'hero' ? '📍 To' : '📍 Drop Location'}
            </label>
            <CityInput
              label=""
              placeholder="e.g. Mumbai, Dwarka..."
              value={form.drop}
              onChange={(val) => set('drop', val)}
              required
              error={errors.drop}
              inputId="drop-location"
            />
          </div>,
        )}

        {fieldWrap(
          'date',
          <div className={`flex-1 min-w-0 ${variant === 'hero' ? 'bg-gray-50 border-[1.5px] border-gray-100 rounded-xl px-3 py-2' : ''}`}>
            <label className={fieldLabelClass} htmlFor="pickup-date-input">
              {variant === 'hero' ? '📅 Date' : '📅 Pickup Date'}
            </label>
            <div className="relative">
              <CalendarBlank
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-[#E8651A] pointer-events-none"
              />
              <Input
                id="pickup-date-input"
                type="date"
                required
                min={todayStr()}
                className={`${inputClass('date')} pl-9`}
                value={form.date}
                onChange={(e) => set('date', e.target.value)}
                data-testid="pickup-date-input"
              />
            </div>
            {errors.date && <p className="text-[11px] text-[#E53935] mt-1">⚠ {errors.date}</p>}
          </div>,
        )}

        {fieldWrap(
          'vehicle',
          <div className={`flex-1 min-w-0 ${variant === 'hero' ? 'bg-gray-50 border-[1.5px] border-gray-100 rounded-xl px-3 py-2' : ''}`}>
            <label className={fieldLabelClass}>{variant === 'hero' ? '🚗 Vehicle' : '🚗 Vehicle'}</label>
            <Select value={form.vehicle} onValueChange={(v) => set('vehicle', v)}>
              <SelectTrigger className={selectTriggerClass('vehicle')} data-testid="vehicle-type-select">
                <SelectValue placeholder="Select vehicle" />
              </SelectTrigger>
              <SelectContent>
                {bookingOptions.map((v) => (
                  <SelectItem key={v.id || v.value} value={v.value}>
                    {v.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.vehicle && <p className="text-[11px] text-[#E53935] mt-1">⚠ {errors.vehicle}</p>}
          </div>,
        )}
      </div>

      {/* ROW 2 — Contact & passengers */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4">
        {fieldWrap(
          'name',
          <div>
            <label className={fieldLabelClass} htmlFor="booking-name-input">
              👤 Name
            </label>
            <Input
              id="booking-name-input"
              required
              className={inputClass('name')}
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              data-testid="name-input"
            />
            {errors.name && <p className="text-[11px] text-[#E53935] mt-1">⚠ {errors.name}</p>}
          </div>,
        )}

        {fieldWrap(
          'phone',
          <div>
            <label className={fieldLabelClass} htmlFor="booking-phone-input">
              📞 Phone
            </label>
            <Input
              id="booking-phone-input"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              maxLength={14}
              required
              placeholder="10-digit mobile"
              className={inputClass('phone')}
              value={form.phone}
              onChange={(e) => set('phone', e.target.value)}
              data-testid="phone-input"
            />
            {errors.phone && <p className="text-[11px] text-[#E53935] mt-1">⚠ {errors.phone}</p>}
          </div>,
        )}

        {fieldWrap(
          'passengers',
          <div>
            <label className={fieldLabelClass}>👥 Passengers</label>
            <Select value={form.passengers} onValueChange={(v) => set('passengers', v)}>
              <SelectTrigger className={selectTriggerClass('passengers')}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[1, 2, 3, 4, 5, 6, 7, 8, 10, 12].map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.passengers && <p className="text-[11px] text-[#E53935] mt-1">⚠ {errors.passengers}</p>}
          </div>,
        )}
      </div>

      <details className="mb-4">
        <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600 font-medium select-none">
          + Add special requests / notes (optional)
        </summary>
        <div className="mt-2 space-y-3">
          <div>
            <label className={fieldLabelClass}>Email</label>
            <Input
              type="email"
              className={inputClass('email')}
              placeholder="you@email.com"
              value={form.email}
              onChange={(e) => set('email', e.target.value)}
            />
          </div>
          <div>
            <label className={fieldLabelClass}>Notes</label>
            <Textarea
              className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#E8651A]/30 focus:border-[#E8651A] transition-all duration-150 placeholder:text-gray-300"
              rows={3}
              placeholder="Any special requests..."
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
            />
          </div>
        </div>
      </details>

      <div className="mx-auto mt-4 flex w-full max-w-md flex-col gap-2 sm:max-w-none sm:flex-row sm:items-stretch sm:justify-center">
        <button
          type="button"
          onClick={handleInstantQuote}
          className="flex flex-1 items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-bold px-6 py-3.5 rounded-xl text-sm transition-all duration-200 hover:-translate-y-0.5 shadow-md shadow-green-100 sm:max-w-xs"
          data-testid="submit-booking-button"
        >
          <svg width="16" height="16" fill="white" viewBox="0 0 24 24" aria-hidden>
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
            <path d="M12 0C5.373 0 0 5.373 0 12c0 2.123.553 4.116 1.522 5.847L0 24l6.335-1.502A11.954 11.954 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.898 0-3.68-.487-5.23-1.341l-.375-.214-3.762.893.953-3.67-.234-.389A9.946 9.946 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z" />
          </svg>
          Get Instant Quote on WhatsApp
        </button>

        <a
          href={`tel:${BRAND.contact.phone}`}
          className="flex flex-1 items-center justify-center gap-2 bg-[#E8651A] hover:bg-[#C05A18] text-white font-bold px-6 py-3.5 rounded-xl text-sm transition-all duration-200 hover:-translate-y-0.5 shadow-md shadow-orange-900/20 sm:max-w-xs"
        >
          <Phone size={18} weight="bold" />
          Call Now
        </a>
      </div>

      <div className="flex flex-wrap items-center justify-center gap-4 py-3 border-t border-gray-100 mt-4">
        <div className="flex items-center gap-1.5">
          <span className="text-yellow-400 text-sm">★★★★★</span>
          <span className="text-xs text-gray-500 font-medium">4.9 · 200+ Google Reviews</span>
        </div>
        <span className="text-gray-200 hidden sm:block">|</span>
        <div className="flex items-center gap-1.5">
          <span className="text-green-500 text-xs font-bold">✓</span>
          <span className="text-xs text-gray-500 font-medium">Verified Business</span>
        </div>
        <span className="text-gray-200 hidden sm:block">|</span>
        <div className="flex items-center gap-1.5">
          <span className="text-xs">🔒</span>
          <span className="text-xs text-gray-500 font-medium">Free Cancellation</span>
        </div>
      </div>
    </form>
  );
};

export default CabBookingForm;
